/**
 * AdTelemetryBridge — connects VastOutstreamPlayer emitter events to AdTelemetry.
 *
 * Maps the player's AdEvents to the equivalent AdTelemetry calls, mirroring
 * the logic in timepay-ui's AdPlayer.jsx which listens to Google IMA SDK events.
 *
 * IMA SDK event → vast-outstream-player AdEvent → AdTelemetry call
 * ─────────────────────────────────────────────────────────────────
 * (init)                    AD_REQUESTED       adLifecycleEvent('ad_attempted')
 * IMA.STARTED               STARTED            ensureImpression + adProgressEvent('start')
 * IMA.FIRST_QUARTILE        FIRST_QUARTILE     adProgressEvent('progress-25')
 * IMA.MIDPOINT              MIDPOINT           adProgressEvent('progress-50')
 * IMA.THIRD_QUARTILE        THIRD_QUARTILE     adProgressEvent('progress-75')
 * IMA.COMPLETE              COMPLETE           adProgressEvent('complete')
 * mute button               MUTED              adInteractionEvent('click', label:'mute')
 * unmute button             UNMUTED            adInteractionEvent('click', label:'unmute')
 * skip button               SKIPPED            adInteractionEvent('click', label:'skip')
 * video click               CLICKED            adInteractionEvent('click', label:'clickthrough')
 * IMA.AD_ERROR              ERROR              adErrorEvent
 * visibilitychange→hidden   (browser)          adDisengagementEvent('tabOutOfFocus')
 * pagehide / beforeunload   (browser)          adDisengagementEvent('tabClosed')
 *
 * Usage:
 *   const emitter = new AdEventEmitter();
 *   const bridge  = createAdTelemetryBridge({ emitter, timePayVariant: 'v1' });
 *   // ... later, when component unmounts:
 *   bridge.destroy();
 */

import { AdEvents } from '../lib/AdEvents';
import adTelemetry  from './AdTelemetry';

export function createAdTelemetryBridge({ emitter, timePayVariant = '' }) {
  // Track current ad state so disengagement events have the right context
  let currentAdId   = 'unknown';
  let currentTimeMs = 0;
  let isStarted     = false;

  // ---- Helpers ----------------------------------------------------------------

  const getId  = (payload) => payload?.adId || currentAdId;
  const getMs  = (payload) => (typeof payload?.currentTimeMs === 'number' ? payload.currentTimeMs : currentTimeMs);
  const getDur = (payload) => (typeof payload?.durationMs    === 'number' ? payload.durationMs    : currentTimeMs);

  const syncState = (payload) => {
    if (payload?.adId)            currentAdId   = payload.adId;
    if (payload?.currentTimeMs != null) currentTimeMs = payload.currentTimeMs;
  };

  // ---- Lifecycle --------------------------------------------------------------

  const onRequested = () => {
    adTelemetry.adLifecycleEvent({ action: 'ad_attempted', timePayVariant });
  };

  // ---- Progress ---------------------------------------------------------------

  const onStarted = (payload) => {
    syncState(payload);
    isStarted = true;
    adTelemetry.ensureImpression({ adId: getId(payload), timePayVariant });
    adTelemetry.adProgressEvent({ adId: getId(payload), action: 'start', time: 0, timePayVariant });
  };

  const onFirstQuartile = (payload) => {
    syncState(payload);
    adTelemetry.adProgressEvent({ adId: getId(payload), action: 'progress-25', time: getMs(payload), timePayVariant });
  };

  const onMidpoint = (payload) => {
    syncState(payload);
    adTelemetry.adProgressEvent({ adId: getId(payload), action: 'progress-50', time: getMs(payload), timePayVariant });
  };

  const onThirdQuartile = (payload) => {
    syncState(payload);
    adTelemetry.adProgressEvent({ adId: getId(payload), action: 'progress-75', time: getMs(payload), timePayVariant });
  };

  const onComplete = (payload) => {
    syncState(payload);
    adTelemetry.adProgressEvent({ adId: getId(payload), action: 'complete', time: getDur(payload), timePayVariant });
  };

  // ---- Interactions -----------------------------------------------------------

  const onMuted = (payload) => {
    syncState(payload);
    adTelemetry.adInteractionEvent({ adId: getId(payload), action: 'click', label: 'mute', time: getMs(payload), timePayVariant });
  };

  const onUnmuted = (payload) => {
    syncState(payload);
    adTelemetry.adInteractionEvent({ adId: getId(payload), action: 'click', label: 'unmute', time: getMs(payload), timePayVariant });
  };

  const onSkipped = (payload) => {
    syncState(payload);
    adTelemetry.adInteractionEvent({ adId: getId(payload), action: 'click', label: 'skip', time: getMs(payload), timePayVariant });
  };

  const onClicked = (payload) => {
    syncState(payload);
    adTelemetry.adInteractionEvent({ adId: getId(payload), action: 'click', label: 'clickthrough', time: getMs(payload), timePayVariant });
  };

  // ---- Error ------------------------------------------------------------------

  const onError = (payload) => {
    adTelemetry.adErrorEvent({
      adId:         getId(payload),
      errorCode:    String(payload?.code    ?? 'ERR_UNKNOWN'),
      errorMessage: payload?.message ?? 'Unknown error',
    });
  };

  // ---- Disengagement (browser-level, same as timepay-ui) ----------------------

  const sendDisengagement = (action) => {
    if (!isStarted) return;
    adTelemetry.adDisengagementEvent({ adId: currentAdId, action, time: currentTimeMs, timePayVariant });
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') sendDisengagement('tabOutOfFocus');
  };

  const onPageHide = () => sendDisengagement('tabClosed');

  // ---- Register all listeners -------------------------------------------------

  emitter.on(AdEvents.AD_REQUESTED,        onRequested);
  emitter.on(AdEvents.STARTED,             onStarted);
  emitter.on(AdEvents.FIRST_QUARTILE,      onFirstQuartile);
  emitter.on(AdEvents.MIDPOINT,            onMidpoint);
  emitter.on(AdEvents.THIRD_QUARTILE,      onThirdQuartile);
  emitter.on(AdEvents.COMPLETE,            onComplete);
  emitter.on(AdEvents.MUTED,               onMuted);
  emitter.on(AdEvents.UNMUTED,             onUnmuted);
  emitter.on(AdEvents.SKIPPED,             onSkipped);
  emitter.on(AdEvents.CLICKED,             onClicked);
  emitter.on(AdEvents.ERROR,               onError);

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide',           onPageHide);
  window.addEventListener('beforeunload',       onPageHide);

  // ---- Teardown ---------------------------------------------------------------

  return {
    destroy() {
      emitter.off(AdEvents.AD_REQUESTED,        onRequested);
      emitter.off(AdEvents.STARTED,             onStarted);
      emitter.off(AdEvents.FIRST_QUARTILE,      onFirstQuartile);
      emitter.off(AdEvents.MIDPOINT,            onMidpoint);
      emitter.off(AdEvents.THIRD_QUARTILE,      onThirdQuartile);
      emitter.off(AdEvents.COMPLETE,            onComplete);
      emitter.off(AdEvents.MUTED,               onMuted);
      emitter.off(AdEvents.UNMUTED,             onUnmuted);
      emitter.off(AdEvents.SKIPPED,             onSkipped);
      emitter.off(AdEvents.CLICKED,             onClicked);
      emitter.off(AdEvents.ERROR,               onError);

      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide',           onPageHide);
      window.removeEventListener('beforeunload',       onPageHide);
    },
  };
}
